
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getJobs, getFilterOptions, type FilterOptions } from '@/api';
import { LegacyJob } from '../types';
import { useCities } from '../src/contexts/CitiesContext';
import JobRow from './JobRow';
import SearchWithSuggestions from './SearchWithSuggestions';
import EmptyState from './EmptyState';

// 使用 LegacyJob 类型用于向后兼容
type Job = LegacyJob;

// 筛选组组件 - 统一样式和颜色
interface FilterItem {
  id: string;
  label: string;
  count: number;
  icon?: React.ReactNode;
}

interface FilterGroupProps {
  title: string;
  items: FilterItem[];
  selectedItems: string[];
  onToggle: (value: string) => void;
  totalCount: number;
  withIcon?: boolean;
  allLabel: string;
}

const FilterGroup: React.FC<FilterGroupProps> = ({ title, items, selectedItems, onToggle, totalCount, withIcon = false, allLabel }) => {
  return (
    <div className="space-y-3 mb-5">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{title}</h3>
      <div className="flex flex-col gap-1">
        {/* All Option - 固定不滚动 */}
        <button
          onClick={() => onToggle('')}
          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between group min-w-0 ${
            selectedItems.length === 0 ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'
          }`}
        >
          <span className="truncate">{allLabel}</span>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-xs ${selectedItems.length === 0 ? 'text-blue-100' : 'text-slate-400'}`}>{totalCount}</span>
            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
              selectedItems.length === 0 ? 'bg-white border-white' : 'border-slate-300'
            }`}>
              {selectedItems.length === 0 && (
                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
        </button>
        {/* Other Options - 可滚动 */}
        <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => onToggle(item.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between group min-w-0 ${
                selectedItems.includes(item.id) ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                {withIcon && <span className="text-slate-400 shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs ${selectedItems.includes(item.id) ? 'text-blue-100' : 'text-slate-400'}`}>{item.count}</span>
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  selectedItems.includes(item.id) ? 'bg-white border-white' : 'border-slate-300'
                }`}>
                  {selectedItems.includes(item.id) && (
                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

interface FullJobListProps {
  onBack: () => void;
  onSelectJob: (job: Job) => void;
  initialTypeFilter?: string;
  initialWorkStyleFilter?: string;
  searchQuery?: string;
}

const FullJobList: React.FC<FullJobListProps> = ({ onBack, onSelectJob, initialTypeFilter, initialWorkStyleFilter, searchQuery }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { cities: allCities, citiesMap } = useCities();

  // API loaded jobs state
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load jobs and filter options from API on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsData, optionsData] = await Promise.all([
          getJobs(),
          getFilterOptions()
        ]);
        // 优先显示急招岗位 (priority: 'HIGH')
        const urgentJobs = jobsData.filter(job => job.priority === 'HIGH');
        const otherJobs = jobsData.filter(job => job.priority !== 'HIGH');
        const sortedJobs = [...urgentJobs, ...otherJobs];
        setJobs(sortedJobs);
        setFilterOptions(optionsData);
      } catch (err) {
        console.error('Failed to load jobs:', err);
        setError('Failed to load jobs');
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 翻译辅助函数
  const getDepartmentLabel = (dept: string) => {
    const deptMap: Record<string, string> = {
      'Engineering': 'engineering',
      'Product': 'product',
      'Marketing': 'marketing',
      'Sales': 'sales',
      'Operations': 'operations',
      'Business Development': 'businessDevelopment',
      'Data': 'data',
      'Finance': 'finance',
      'Campus Ambassador': 'campusAmbassador',
      'Human Resources': 'hr'
    };
    const key = deptMap[dept] || dept.toLowerCase().replace(/\s+/g, '');
    return t(`jobData.departments.${key}`, dept);
  };

  const getJobTypeLabel = (type: string) => {
    // 将 jobs 中的英文 type 映射到 i18n key
    const typeMap: Record<string, string> = {
      'FULL_TIME': 'fullTime',
      'PART_TIME': 'partTime',
      'INTERNSHIP_ONLINE': 'internshipOnline',
      'INTERNSHIP_OFFLINE': 'internshipOffline'
    };
    const key = typeMap[type] || 'fullTime';
    return t(`jobData.jobTypes.${key}`, type);
  };

  const getCityName = (city: { name?: string; enName?: string; nameZh?: string; nameEn?: string }) => {
    const zh = city.nameZh || city.name || '';
    const en = city.nameEn || city.enName || '';
    return currentLang.startsWith('zh') ? zh : en;
  };

  const [searchTerm, setSearchTerm] = useState('');

  // 所有筛选项改为数组，支持多选
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedWorkStyles, setSelectedWorkStyles] = useState<string[]>([]);

  // 移动端筛选抽屉状态
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // 移动端筛选折叠状态
  const [expandedSections, setExpandedSections] = useState({
    cities: true,
    departments: true,
    types: true,
    workStyles: true
  });

  // 切换筛选折叠状态
  const toggleSection = (section: 'cities' | 'departments' | 'types' | 'workStyles') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 切换筛选（单选 - 全部和具体选项互斥，再次点击取消选中）
  const toggleCity = (city: string) => {
    if (city === '') {
      // 点击"全部"，清空选择
      setSelectedCities([]);
    } else if (selectedCities.includes(city)) {
      // 已选中，再次点击取消选中
      setSelectedCities([]);
    } else {
      // 点击具体选项，只选中该选项
      setSelectedCities([city]);
    }
  };

  const toggleDept = (dept: string) => {
    if (dept === '') {
      setSelectedDepts([]);
    } else if (selectedDepts.includes(dept)) {
      setSelectedDepts([]);
    } else {
      setSelectedDepts([dept]);
    }
  };

  const toggleType = (type: string) => {
    if (type === '') {
      setSelectedTypes([]);
    } else if (selectedTypes.includes(type)) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes([type]);
    }
  };

  const toggleWorkStyle = (style: string) => {
    if (style === '') {
      setSelectedWorkStyles([]);
    } else if (selectedWorkStyles.includes(style)) {
      setSelectedWorkStyles([]);
    } else {
      setSelectedWorkStyles([style]);
    }
  };

  // 处理初始类型筛选（从 "View Remote Jobs" 入口进入）
  useEffect(() => {
    if (initialTypeFilter) {
      setSelectedTypes([initialTypeFilter]);
    }
  }, [initialTypeFilter]);

  // 处理初始工作模式筛选（从 "View Home-based Jobs" 入口进入）
  useEffect(() => {
    if (initialWorkStyleFilter) {
      setSelectedWorkStyles([initialWorkStyleFilter]);
    }
  }, [initialWorkStyleFilter]);

  // 处理初始搜索查询（从首页搜索进入）
  useEffect(() => {
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, [searchQuery]);

  // 简化的计数函数 - 使用 API 返回的数据
  const getCount = useCallback((key: 'city' | 'dept' | 'type' | 'workStyle', value: string) => {
    if (!filterOptions) return 0;

    if (value === 'all') {
      // 返回符合条件的总职位数
      return jobs.length;
    }

    if (key === 'city') {
      return filterOptions.totals.byCity[value]?.count || 0;
    }
    if (key === 'dept') {
      return filterOptions.totals.byDepartment[value]?.count || 0;
    }
    if (key === 'type') {
      return filterOptions.totals.byType[value]?.count || 0;
    }
    if (key === 'workStyle') {
      return filterOptions.totals.byWorkStyle[value]?.count || 0;
    }
    return 0;
  }, [filterOptions, jobs]);

  // 计算可选的城市列表 - 使用 API 数据
  const availableCities = useMemo(() => {
    if (!filterOptions || filterOptions.cities.length === 0) {
      return allCities;
    }

    // 合并城市信息和 API 计数
    const cityMap = new Map(filterOptions.cities.map(c => [c.id, c]));
    return allCities
      .map(city => ({
        ...city,
        jobCount: cityMap.get(city.id)?.count || 0
      }))
      .filter(city => city.jobCount > 0) // 只显示有职位的城市
      .sort((a, b) => b.jobCount - a.jobCount);
  }, [filterOptions, allCities]);

  // 计算可选的岗位类别 - 使用 API 数据
  const availableDepts = useMemo(() => {
    if (!filterOptions || filterOptions.departments.length === 0) {
      return [];
    }
    return filterOptions.departments.map(d => d.title);
  }, [filterOptions]);

  // 最终过滤后的岗位列表
  const filteredJobs = useMemo(() => {
    const result = jobs.filter(job => {
      const matchesSearch = (job.title + job.location + job.department).toLowerCase().includes(searchTerm.toLowerCase());
      // 城市匹配：检查 city 字段或 cities 数组
      const matchesCity = selectedCities.length === 0 ||
        selectedCities.includes(job.city) ||
        (job.cities && job.cities.some(c => selectedCities.includes(c)));
      const matchesDept = selectedDepts.length === 0 || selectedDepts.includes(job.department);
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(job.type);
      const matchesWorkStyle = selectedWorkStyles.length === 0 || selectedWorkStyles.includes(job.workStyle);
      return matchesSearch && matchesCity && matchesDept && matchesType && matchesWorkStyle;
    });
    return result;
  }, [searchTerm, selectedCities, selectedDepts, selectedTypes, selectedWorkStyles, jobs]);

  // 工作类型列表 - 使用 API 数据
  const workTypes = useMemo(() => {
    if (!filterOptions || filterOptions.types.length === 0) {
      return [];
    }

    const typeMap: Record<string, string> = {
      'FULL_TIME': 'fullTime',
      'PART_TIME': 'partTime',
      'INTERNSHIP_ONLINE': 'internshipOnline',
      'INTERNSHIP_OFFLINE': 'internshipOffline'
    };

    return filterOptions.types
      .filter(t => t.count > 0) // 只显示有职位的类型
      .map(type => ({
        value: type.title,
        label: t(`jobData.jobTypes.${typeMap[type.title] || 'fullTime'}`, type.title),
        jobCount: type.count
      }))
      .sort((a, b) => b.jobCount - a.jobCount);
  }, [filterOptions, currentLang, t]);

  // 工作模式列表 - 使用 API 数据
  const workStyles = useMemo(() => {
    if (!filterOptions || filterOptions.workStyles.length === 0) {
      return [];
    }

    const styleConfig: Record<string, { labelKey: string; iconName: string }> = {
      'ONSITE': { labelKey: 'onsite', iconName: 'location' },
      'HOME_BASED': { labelKey: 'homeBased', iconName: 'home' },
      'REMOTE': { labelKey: 'remote', iconName: 'home' },
      'HYBRID': { labelKey: 'hybrid', iconName: 'calendar' }
    };

    return filterOptions.workStyles
      .filter(s => s.count > 0) // 只显示有职位的工作模式
      .map(style => ({
        value: style.title,
        iconName: styleConfig[style.title]?.iconName || 'location',
        label: t(`jobData.workStyles.${styleConfig[style.title]?.labelKey || 'onsite'}`),
        jobCount: style.count
      }))
      .sort((a, b) => b.jobCount - a.jobCount);
  }, [filterOptions, currentLang, t]);

  // 清除所有筛选
  const clearAllFilters = () => {
    setSelectedCities([]);
    setSelectedDepts([]);
    setSelectedTypes([]);
    setSelectedWorkStyles([]);
    setSearchTerm('');
  };

  // 推荐职位 - 取前 3 个急招职位或最新职位
  const recommendedJobs = useMemo(() => {
    if (filteredJobs.length > 0) return [];

    // 优先推荐急招职位
    const urgentJobs = jobs.filter(job => job.priority === 'HIGH').slice(0, 3);
    if (urgentJobs.length > 0) return urgentJobs;

    // 如果没有急招职位，返回最新发布的 3 个职位
    const sortedByDate = [...jobs].sort((a, b) => {
      const dateA = a.postedDate instanceof Date ? a.postedDate : new Date(a.postedDate);
      const dateB = b.postedDate instanceof Date ? b.postedDate : new Date(b.postedDate);
      return dateB.getTime() - dateA.getTime();
    });
    return sortedByDate.slice(0, 3);
  }, [filteredJobs, jobs]);

  // 检查是否有任何筛选激活
  const hasActiveFilters = selectedCities.length > 0 ||
    selectedDepts.length > 0 || selectedTypes.length > 0 || selectedWorkStyles.length > 0 || searchTerm !== '';

  return (
    <div className="bg-slate-50 min-h-screen pt-16">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 px-6 py-10">

        {/* Left Sidebar: Filters - Desktop Only */}
        <aside className="hidden lg:block lg:w-[280px] shrink-0 space-y-8">
          <div className="sticky top-24 space-y-8">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm min-w-0">
              <div className="flex items-center justify-between mb-5 gap-2">
                <h2 className="text-base font-bold text-slate-900 truncate">{t('fullJobList.mobileFilters')}</h2>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer shrink-0 whitespace-nowrap"
                  >
                    {t('fullJobList.clearFilters')}
                  </button>
                )}
              </div>

              {/* City Group */}
              <FilterGroup
                title={t('fullJobList.filters.cities')}
                items={availableCities.map(c => ({ id: c.id, label: getCityName(c), count: getCount('city', c.id) }))}
                selectedItems={selectedCities}
                onToggle={toggleCity}
                totalCount={getCount('city', 'all')}
                allLabel={t('fullJobList.all')}
              />

              {/* Department Group */}
              <FilterGroup
                title={t('fullJobList.filters.departments')}
                items={availableDepts.map(d => ({ id: d, label: getDepartmentLabel(d), count: getCount('dept', d) }))}
                selectedItems={selectedDepts}
                onToggle={toggleDept}
                totalCount={getCount('dept', 'all')}
                allLabel={t('fullJobList.all')}
              />

              {/* Work Type Group */}
              <FilterGroup
                title={t('fullJobList.filters.types')}
                items={workTypes.map(t => ({ id: t.value, label: t.label, count: getCount('type', t.value) }))}
                selectedItems={selectedTypes}
                onToggle={toggleType}
                totalCount={getCount('type', 'all')}
                allLabel={t('fullJobList.all')}
              />

              {/* Work Style Group */}
              <FilterGroup
                title={t('fullJobList.filters.workStyles')}
                items={workStyles.map(s => ({
                  id: s.value,
                  label: s.label,
                  count: getCount('workStyle', s.value),
                  icon: s.iconName === 'location' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  )
                }))}
                selectedItems={selectedWorkStyles}
                onToggle={toggleWorkStyle}
                totalCount={getCount('workStyle', 'all')}
                allLabel={t('fullJobList.all')}
                withIcon
              />
            </div>
          </div>
        </aside>

        {/* Mobile Filter Button */}
        {isMobile && (
          <button
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden fixed bottom-24 right-6 z-40 bg-blue-600 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all cursor-pointer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold">
                {selectedCities.length + selectedDepts.length + selectedTypes.length + selectedWorkStyles.length}
              </span>
            )}
          </button>
        )}

        {/* Mobile Filter Drawer */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowMobileFilters(false)}
            ></div>

            {/* Bottom Sheet */}
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-100 shrink-0">
                <h2 className="text-xl font-black text-slate-900 tracking-tight">{t('fullJobList.mobileFilters')}</h2>
                <div className="flex items-center gap-3">
                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 cursor-pointer"
                    >
                      {t('fullJobList.resetAll')}
                    </button>
                  )}
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors cursor-pointer"
                  >
                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* City Group */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleSection('cities')}
                    className="w-full flex items-center justify-between py-2 px-1 cursor-pointer"
                  >
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('fullJobList.filters.cities')}</h3>
                    <svg
                      className={`w-4 h-4 text-slate-400 transition-transform ${expandedSections.cities ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.cities && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => toggleCity('')}
                        className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${selectedCities.length === 0 ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                      >
                        <span>{t('fullJobList.all')}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] ${selectedCities.length === 0 ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                            {getCount('city', 'all')}
                          </span>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedCities.length === 0 ? 'bg-white border-white' : 'border-slate-300'}`}>
                            {selectedCities.length === 0 && (
                              <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </button>
                      {availableCities.map(c => {
                        const isSelected = selectedCities.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            onClick={() => toggleCity(c.id)}
                            className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                          >
                            <span>{getCityName(c)}</span>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                                {getCount('city', c.id)}
                              </span>
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-white border-white' : 'border-slate-300'}`}>
                                {isSelected && (
                                  <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Department Group */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleSection('departments')}
                    className="w-full flex items-center justify-between py-2 px-1 cursor-pointer"
                  >
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('fullJobList.filters.departments')}</h3>
                    <svg
                      className={`w-4 h-4 text-slate-400 transition-transform ${expandedSections.departments ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.departments && (
                    <div className="flex flex-col gap-1">
                    <button
                      onClick={() => toggleDept('')}
                      className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${selectedDepts.length === 0 ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                    >
                      <span>{t('fullJobList.all')}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] ${selectedDepts.length === 0 ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                          {getCount('dept', 'all')}
                        </span>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedDepts.length === 0 ? 'bg-white border-white' : 'border-slate-300'}`}>
                          {selectedDepts.length === 0 && (
                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                    {availableDepts.map(d => {
                      const isSelected = selectedDepts.includes(d);
                      return (
                        <button
                          key={d}
                          onClick={() => toggleDept(d)}
                          className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                        >
                          <span>{getDepartmentLabel(d)}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                              {getCount('dept', d)}
                            </span>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-white border-white' : 'border-slate-300'}`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>

                {/* Work Type Group */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleSection('types')}
                    className="w-full flex items-center justify-between py-2 px-1 cursor-pointer"
                  >
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('fullJobList.filters.types')}</h3>
                    <svg
                      className={`w-4 h-4 text-slate-400 transition-transform ${expandedSections.types ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.types && (
                    <div className="flex flex-col gap-1">
                    <button
                      onClick={() => toggleType('')}
                      className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${selectedTypes.length === 0 ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                    >
                      <span>{t('fullJobList.all')}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] ${selectedTypes.length === 0 ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                          {getCount('type', 'all')}
                        </span>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedTypes.length === 0 ? 'bg-white border-white' : 'border-slate-300'}`}>
                          {selectedTypes.length === 0 && (
                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                    {workTypes.map(t => {
                      const isSelected = selectedTypes.includes(t.value);
                      return (
                        <button
                          key={t.value}
                          onClick={() => toggleType(t.value)}
                          className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                        >
                          <span>{t.label}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                              {getCount('type', t.value)}
                            </span>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-white border-white' : 'border-slate-300'}`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>

                {/* Work Style Group */}
                <div className="space-y-2">
                  <button
                    onClick={() => toggleSection('workStyles')}
                    className="w-full flex items-center justify-between py-2 px-1 cursor-pointer"
                  >
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('fullJobList.filters.workStyles')}</h3>
                    <svg
                      className={`w-4 h-4 text-slate-400 transition-transform ${expandedSections.workStyles ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.workStyles && (
                    <div className="flex flex-col gap-1">
                    <button
                      onClick={() => toggleWorkStyle('')}
                      className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${selectedWorkStyles.length === 0 ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                    >
                      <span>{t('fullJobList.all')}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] ${selectedWorkStyles.length === 0 ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                          {getCount('workStyle', 'all')}
                        </span>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedWorkStyles.length === 0 ? 'bg-white border-white' : 'border-slate-300'}`}>
                          {selectedWorkStyles.length === 0 && (
                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                    {workStyles.map(style => {
                      const isSelected = selectedWorkStyles.includes(style.value);
                      return (
                        <button
                          key={style.value}
                          onClick={() => toggleWorkStyle(style.value)}
                          className={`w-full text-left px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group min-h-[44px] ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="w-5 h-5">
                              {style.iconName === 'location' ? (
                                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              ) : (
                                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                              )}
                            </span>
                            <span>{style.label}</span>
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-600'}`}>
                              {getCount('workStyle', style.value)}
                            </span>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-white border-white' : 'border-slate-300'}`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>

              {/* Apply Button */}
              <div className="p-6 border-t border-slate-100 shrink-0">
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-full bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-sm hover:bg-blue-600 active:scale-95 transition-all cursor-pointer"
                >
                  {t('fullJobList.resultsCount', { count: filteredJobs.length })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Right Content: Search & List */}
        <main className="flex-1 space-y-4 sm:space-y-6 min-w-0">
          {/* Top Search Bar Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6 mb-4 sm:mb-6 lg:mb-8">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{t('fullJobList.title')}</h1>
                <p className="text-slate-500 font-medium text-sm mt-1">
                  {t('fullJobList.subtitle')}
                  {hasActiveFilters && (
                    <span className="ml-2 text-blue-600">
                    </span>
                  )}
                </p>
              </div>
            </div>

            <SearchWithSuggestions
              jobs={jobs}
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('fullJobList.searchPlaceholder')}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className="mt-6 flex flex-wrap gap-2">
                {selectedCities.map(c => {
                  const city = citiesMap.get(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleCity(c)}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold hover:bg-indigo-200 transition-all cursor-pointer"
                    >
                      {city ? getCityName(city) : c}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  );
                })}
                {selectedDepts.map(d => (
                  <button
                    key={d}
                    onClick={() => toggleDept(d)}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold hover:bg-emerald-200 transition-all cursor-pointer"
                  >
                    {getDepartmentLabel(d)}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ))}
                {selectedTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold hover:bg-amber-200 transition-all cursor-pointer"
                  >
                    {getJobTypeLabel(type)}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ))}
                {selectedWorkStyles.map(s => (
                  <button
                    key={s}
                    onClick={() => toggleWorkStyle(s)}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold hover:bg-purple-200 transition-all cursor-pointer"
                  >
                    {t(`jobData.workStyles.${s === 'onsite' ? 'onsite' : s === 'hybrid' ? 'hybrid' : s === 'remote' ? 'remote' : 'homeBased'}`)}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Job List Container */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden divide-y divide-slate-50">
            {filteredJobs.length > 0 ? (
              filteredJobs.map(job => (
                <JobRow key={job.id} job={job} onSelect={onSelectJob} />
              ))
            ) : (
              <EmptyState
                onClearFilters={clearAllFilters}
                recommendedJobs={recommendedJobs}
                onSelectJob={onSelectJob}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default FullJobList;
